import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Flex, InputNumber, Card, Col, Row, Statistic, Radio, Button, Spin, ConfigProvider, DatePicker } from 'antd';
import { format, parse, setDefaultOptions } from 'date-fns'
import { de } from 'date-fns/locale'
import deDE from 'antd/es/date-picker/locale/de_DE';
import electricityPrices from './spot_price.json';

import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Chart from "react-apexcharts";
import * as utils from './utils';

const { RangePicker } = DatePicker;

const localeDe = {
  ...deDE,
  lang: {
    shortWeekDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    shortMonths: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],
    ...deDE.lang,
  },
}

setDefaultOptions({ locale: de })

const formatsAvg = {
  hourly: "HH:mm",
  daily: "do",
  weekly: "eeee",
  monthly: "MMM",
  quarterly: "QQQ",
  yearly: "yyyy",
};

const formatsYear = {
  daily: "d.M.yy",
  weekly: "'KW'w yyyy",
  monthly: "MMM yyyy",
  quarterly: "QQQ yyyy",
  yearly: "yyyy",
};

const AverageUsageChart = ({ data, dateFormatAvg, setDateFormatAvg, maxLoadRange }) => {

  maxLoadRange = Math.round(maxLoadRange)

  const formattedData = utils.groupByDateFormatAverage(data, dateFormatAvg, formatsAvg)

  return (
    <Flex style={{ width: "100%" }} vertical >
      <Radio.Group value={dateFormatAvg} onChange={(e) => setDateFormatAvg(e.target.value)} >
        <Radio.Button value={formatsAvg.hourly}>15min</Radio.Button>
        <Radio.Button value={formatsAvg.daily}>Tag</Radio.Button>
        <Radio.Button value={formatsAvg.weekly}>Woche</Radio.Button>
        <Radio.Button value={formatsAvg.monthly}>Monat</Radio.Button>
        <Radio.Button value={formatsAvg.quarterly}>Quartal</Radio.Button>
        <Radio.Button value={formatsAvg.yearly}>Jahr</Radio.Button>
      </Radio.Group>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart
          data={formattedData}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="dateTime" />
          <YAxis unit="kW" domain={[dataMin => (dataMin < 0 ? dataMin : 0), maxLoadRange]} />
          <Tooltip />
          <Legend verticalAlign="top" height={36} />
          <Area name="Durchschnittsleistung in kW" type="monotone" dataKey="usage" stroke="#82ca9d" fill="#82ca9d" />
        </AreaChart>
      </ResponsiveContainer>
    </Flex>)
}

//cost is in cent
const TotalCostChart = ({ data, dateFormatTotal, setDateFormatTotal, costPerKwhDefault = 10 }) => {
  const [costPerKwh, setCostPerKwh] = useState(costPerKwhDefault);
  const formattedData = utils.groupByDateFormatTotal(data, dateFormatTotal, costPerKwh)
  const totalCost = utils.getTotals(data, costPerKwh)
  const potentialSavings = totalCost.totalCost - totalCost.totalCostSpot;

  return (
    <>
      <Flex justify='center' align='center' vertical>
        <div style={{ fontSize: 30, paddingTop: 72, fontFamily: "Archivo", fontWeight: 900 }}>
          Beschaffungskosten: Spot vs. Fix
        </div>
        <div style={{ paddingLeft: 60, paddingRight: 60, paddingTop: 36, paddingBottom: 12 }}>
          Hier sehen Sie einen Vergleich zwischen den Beschaffungskosten am Spotmarkt (blau) und Ihren aktuellen festen Beschaffungskosten (orange). Sie können Ihre aktuellen Beschaffungskosten hier eintragen:
        </div>
        <Flex align='center'>
          <div style={{ paddingRight: 12, fontWeight: 800 }}>Aktuelle fixe Beschaffungskosten in Cent: </div>
          <InputNumber
            style={{
              width: 150,
            }}
            defaultValue={costPerKwh}
            min="0"
            max="100"
            step="0.1"
            addonAfter="ct"
            onChange={(value) => setCostPerKwh(value)}
          />
        </Flex>
      </Flex>


      <Radio.Group value={dateFormatTotal} onChange={(e) => setDateFormatTotal(e.target.value)} style={{ paddingBottom: 20 }}>
        <Radio.Button value={formatsYear.daily}>Tag</Radio.Button>
        <Radio.Button value={formatsYear.weekly}>Kalenderwoche</Radio.Button>
        <Radio.Button value={formatsYear.monthly}>Monat</Radio.Button>
        <Radio.Button value={formatsYear.quarterly}>Quartal</Radio.Button>
        <Radio.Button value={formatsYear.yearly}>Jahr</Radio.Button>

      </Radio.Group>
      <ResponsiveContainer width="100%" height={400} style={{ paddingBottom: 60 }}>

        <AreaChart
          data={formattedData}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="dateTime" />
          <YAxis unit="€" domain={[dataMin => (dataMin < 0 ? dataMin : 0), 'auto']} />
          <Tooltip />
          <Legend verticalAlign="top" height={36} />
          <Area name="Kosten mit Fixpreis in €" type="monotone" dataKey="totalCost" stroke="#F87315" fill="#F87315" />
          <Area name="Kosten am Spotmarkt €" type="monotone" dataKey="totalCostSpot" stroke="#00b4d8" fill="#00b4d8" />
        </AreaChart>
      </ResponsiveContainer>

      <Row gutter={[24, 24]}>
        <Col span={8}>
          <Card bordered={false}>
            <Statistic
              title="Beschaffungskosten (im Zeitfenster) mit Fixpreis"
              value={totalCost.totalCost}
              precision={0}
              suffix="€"
              groupSeparator="."
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false}>
            <Statistic
              title="Beschaffungskosten (im Zeitfenster) mit Spotpreis"
              value={totalCost.totalCostSpot}
              precision={0}
              suffix="€"
              groupSeparator="."
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false}>
            <Statistic
              title="Potenzielle Ersparnis auf dem Spotmarkt (im Zeitfenster)"
              value={totalCost.totalCost - totalCost.totalCostSpot}
              precision={0}
              valueStyle={{
                color: potentialSavings < 0 ? '#cf1322' : '#3f8600',
              }}
              prefix={potentialSavings < 0 ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
              suffix="€"
              groupSeparator="."
            />
          </Card>
        </Col>

      </Row>

    </>
  )

}

const ConsumptionOverview = ({ data }) => {
  return (
    <Row gutter={[24, 24]} style={{ width: "100%" }}>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Stromverbrauch"
            value={utils.totalUsage(data)}
            precision={0}
            groupSeparator="."
            suffix="kWh"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title={[
              `Maximal Leistung`,
              <br />,
              `am ${format(utils.maxLoad(data).dateTime, "dd.MM.yyyy")} um ${format(utils.maxLoad(data).dateTime, "HH:mm")} Uhr`
            ]}
            value={utils.maxLoad(data).maxLoad}
            precision={1}
            groupSeparator="."
            suffix="kW"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Grundlast"
            value={utils.averageLoadNighttime(data)}
            precision={1}
            groupSeparator="."
            suffix="kW"
          />
        </Card>
      </Col>
      <Col span={6}>
        <Card bordered={false}>
          <Statistic
            title="Durchschnittliche Last"
            value={utils.averageLoad(data)}
            precision={1}
            groupSeparator="."
            suffix="kW"
          />
        </Card>
      </Col>
    </Row>
  )

}

const DateRangeSelector = ({ dateRange, minDate, maxDate, handleRangeChange, onReset }) => {
  return (
    <Flex
      gap="small"
      align='center'

    >
      <h2>Zeitfenster: </h2>
      <RangePicker
        format={"DD.MM.YYYY"}
        size={"large"}
        value={[dayjs(dateRange[0]), dayjs(dateRange[1])]}
        onChange={handleRangeChange}
        minDate={dayjs(minDate)}
        maxDate={dayjs(maxDate)}
        allowClear={false}
        locale={localeDe}
      />
      <Button size="large" onClick={onReset}>Zurücksetzten</Button>
    </Flex>


  )

}

const PeakChart = ({ data, handleRangeChange, setDateFormatAvg, setDateFormatTotal }) => {
  const percentiles = utils.calculatePercentiles(data);
  const series = utils.groupByMonthAndMaxUsage(data);

  const chartData = {
    series: series,
    options: {
      yaxis: {
        tooltip: {
          enabled: false
        }
      },
      chart: {
        type: 'heatmap',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        selection: {
          enabled: false,
        },
        events: {
          click: function (event, chartContext, config) {
            try {
              const seriesIndex = config.seriesIndex;
              const dataPointIndex = config.dataPointIndex;

              const day = dataPointIndex + 1
              const yearMonth = chartContext.w.config.series[seriesIndex].name;

              const dateFormat = 'yyyy MMMM dd';
              const date = parse(`${yearMonth} ${day}`, dateFormat, new Date(), { locale: de });

              const dates = [dayjs(date), dayjs(date)]
              handleRangeChange(dates)
              setDateFormatAvg(formatsAvg.hourly)
              setDateFormatTotal(formatsYear.daily)

            } catch (err) {
              // don't care if it fails
            }

          }
        },
      },
      plotOptions: {
        heatmap: {
          colorScale: {
            ranges: [
              {
                from: -Infinity,
                to: percentiles[50],
                color: '#15fc03',
                name: 'Sehr Niedrig',
              },
              {
                from: percentiles[50],
                to: percentiles[75],
                color: '#e1ff00',
                name: 'Niedrig',
              },
              {
                from: percentiles[75],
                to: percentiles[90],
                color: '#ffec00',
                name: 'Moderat',
              },
              {
                from: percentiles[90],
                to: percentiles[99],
                color: '#ff8000',
                name: 'Hoch',
              },
              {
                from: percentiles[99],
                to: Infinity,
                color: '#FF0000',
                name: 'Sehr Hoch',
              },
            ]
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      title: {
        text: 'Lastspitze pro Tag'
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const day = dataPointIndex + 1;
          const yearMonth = w.config.series[seriesIndex].name;
          const dateFormat = 'yyyy MMMM dd';
          const date = parse(`${yearMonth} ${day}`, dateFormat, new Date(), { locale: de });
          const dateString = format(date, 'dd.MM.yyyy');
          const value = series[seriesIndex][dataPointIndex];

          const tooltipHtml = `
          <div style="padding: 12px;">
          <strong>Datum:</strong> ${dateString}<br>
                <strong>Max. Leistung:</strong> ${value}kW
              </div>
            `;

          return tooltipHtml;
        },
      },


    },
  };

  return (
    <Flex style={{ width: "100%" }} justify='center'>
      <Chart
        options={chartData.options}
        series={chartData.series}
        type="heatmap"
        width="1000"
        height="320"
      />
    </Flex>
  );
};

const LoadedComponent = ({ data, clientName }) => {
  const [filteredData, setFilteredData] = useState(data);
  const minDate = new Date(Math.min(...data.map(item => item.dateTime)))
  minDate.setHours(0, 0, 0, 0); // Start of the day
  const maxDate = new Date(Math.max(...data.map(item => item.dateTime)));
  maxDate.setHours(23, 59, 59, 999); // End of the day

  const [dateRange, setDateRange] = useState([minDate, maxDate]);

  const [dateFormatAvg, setDateFormatAvg] = useState(formatsAvg.hourly);
  const [dateFormatTotal, setDateFormatTotal] = useState(formatsYear.daily);

  const handleRangeChange = (dates) => {
    if (dates == null) {
      onReset()
    } else {
      const dateRange = dates.map(date => date.toDate())
      dateRange[0].setHours(0, 0, 0, 0); // Start of the day
      dateRange[1].setHours(23, 59, 59, 999); // End of the day
      setDateRange(dateRange);
      setFilteredData(data.filter(item => item.dateTime >= dateRange[0] && item.dateTime <= dateRange[1]));
    }
  };

  const onReset = () => {
    setDateRange([minDate, maxDate])
    setFilteredData(data)
  }
  const maxLoadRange = utils.maxLoad(filteredData).maxLoad;

  return (

    <Flex style={{
      width: '100%',
      paddingTop: 36,
      paddingRight: 144,
      paddingLeft: 144,

      overflowX: 'hidden',

      boxSizing: 'border-box',

    }} gap="middle" align="center" justify='center' vertical>
      <div style={{ alignSelf: 'end', paddingRight: 48, fontSize: 18, fontWeight: 900 }}>
        {clientName}
      </div>
      <Flex style={{ width: '100%', paddingTop: 12, paddingBottom: 12 }} gap="middle" align="center" justify='center' vertical>
        <div style={{ padding: 0, fontSize: 36, fontFamily: "Archivo", fontWeight: 900 }}>Ihre Stromprofilanalyse</div>
        <div style={{ color: "#F97316", fontStyle: 'italic', fontSize: 24, fontFamily: "Archivo", fontWeight: 900, paddingBottom: 24 }}>powered by enerwise</div>
        <div style={{ fontSize: 18 }}>Die Stromprofilanalyse von enerwise ermöglicht es Ihnen einen ersten Überblick der Daten zu bekommen und Empfehlungen abzuleiten.</div>
      </Flex>
      <PeakChart data={data} handleRangeChange={handleRangeChange} setDateFormatAvg={setDateFormatAvg} setDateFormatTotal={setDateFormatTotal} />
      <DateRangeSelector dateRange={dateRange} minDate={minDate} maxDate={maxDate} handleRangeChange={handleRangeChange} onReset={onReset} />
      <AverageUsageChart data={filteredData} dateFormatAvg={dateFormatAvg} setDateFormatAvg={setDateFormatAvg} maxLoadRange={maxLoadRange} />
      <Flex style={{ paddingBottom: 36, width: "100%" }}>
        <ConsumptionOverview data={filteredData} />
      </Flex>
      <Flex style={{ paddingBottom: 96 }} vertical>
        <TotalCostChart data={filteredData} dateFormatTotal={dateFormatTotal} setDateFormatTotal={setDateFormatTotal} />
      </Flex>
      <a href="https://cal.com/getenerwise/austausch" target="_blank" rel="noopener noreferrer">
        <Button style={{
          fontFamily: "Archivo",
          backgroundColor: "#F97316",
          width: 180,
          height: 60,
          fontSize: 14,
          fontWeight: 900,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }} type="primary" block>
          Kontakt aufnehmen
        </Button>
      </a>
      <div style={{ height: '100px' }}></div>
      <div style={{ fontSize: 18, textAlign: 'center', paddingBottom: '50px' }}>© 2024 – enerwise - <a style={{ color: "#252525" }} target="_blank" href="https://getenerwise.framer.website/impressum">Impressum</a></div>
    </Flex>
  )

}

const ExcelReader = () => {
  const [loading, setLoading] = useState(true)
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fileId = searchParams.get('fileId');
  const [data, setData] = useState([]);

  const [clientName, setClientName] = useState('');

  useEffect(() => {
    setLoading(true)
    fetchFile();
  }, []);

  async function fetchFile() {
    const targetUrl = `https://storage.googleapis.com/enerwise-lastgang/${fileId}.xlsx`;

    const response = await fetch(targetUrl);
    const blob = await response.blob();

    const reader = new FileReader();
    reader.onload = (e) => {
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const rawData = XLSX.utils.sheet_to_json(ws, { raw: false });

      setClientName(rawData[0].name)

      // Ensure data has correct format
      const data = rawData
        .filter(row => {
          const dateTime = new Date(`${row["date"]} ${row["time"]}`);
          if (dateTime.toString() === "Invalid Date") {
            console.error("Invalid Date: ", row["date"], row["time"]);
          }
          return dateTime.toString() !== "Invalid Date";
        })
        .map(row => {
          const dateTime = new Date(`${row["date"]} ${row["time"]}`);
          const roundedDateTime = utils.roundToNearest15(dateTime);
          return {
            dateTime: roundedDateTime,
            usage: utils.parseToFloat(row["usage"])
          };
        });
      const priceLookup = {};
      electricityPrices.forEach(item => {
        priceLookup[item.dateTime] = item;
      });

      const mergedData = data.map(item => {
        const dateTimeString = item.dateTime.toISOString();
        const priceItem = priceLookup[dateTimeString];
        if (priceItem) {
          const spotPrice = priceItem.price;
          return { ...item, spotPrice };
        } else {
          console.log("No price found for: ", item.dateTime.toISOString())
        }
        return item;
      });

      setData(mergedData);
      setLoading(false)
    };
    reader.readAsBinaryString(blob);
  }

  return (
    <>
      {loading ? (
        <Flex style={{ width: "100%", height: "100vh" }} justify='center' align="center" gap="middle">
          <Spin size="large" />
        </Flex>
      ) : (
        <LoadedComponent data={data} clientName={clientName} />
      )}
    </>
  );
};

export default ExcelReader;